import moment from "moment/min/moment-with-locales";
import React, { useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import { mapItemType } from "../components/HelperFunctions";
import {
    useFetchData,
    useLatestCalculatedDate,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latest2Weeks, latestDay, latestMonth, latestQuarter, latestWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import ItemTypesPicker from "../components/Pickers/ItemTypesPicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import SongSearchInput from "../components/SearchInput";
import { useUserFormState } from "../components/UserSelection";
import { itemTypes } from "../components/constants";
import MinuteByMinuteTable from "../components/minuteByMinute/MinuteByMinuteTable";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";
import useSortableData from "../components/useSortableData";

export default function MinuteByMinute() {
    const moduleName = useMemo(() => "minuteByMinute", []);
    const moduleId = useMemo(() => ({ 0: 38, 1: 38, 2: 38 }), []);

    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [filterData, setFilterData] = useState({ itemTypes: itemTypes.filter(x => x.id !== "b"), searchString: "" });

    const advancedInputRef = useRef(null);

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestWeek(latestCalculatedDate, true) }));
            }
            return [
                latestDay(latestCalculatedDate),
                latestWeek(latestCalculatedDate),
                latest2Weeks(latestCalculatedDate),
                latestMonth(latestCalculatedDate),
                latestQuarter(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    const demo = useMemo(() => [{ data_type: 0, listener_group: 0 }], []);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);
    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const input = {
            radio_id: formData.station[0].radio_id,
            min_date: moment(formData.dateRange[0]).format("YYYY-MM-DD"),
            max_date: moment(formData.dateRange[1]).format("YYYY-MM-DD"),
            min_hour: formData.hourRange[0],
            max_hour: formData.hourRange[1],
            days: formData.days,
        };
        await fetchData("/broadcast/range", input);
    }

    const minuteByMinuteDataMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.itemTypes.length) {
                filteredData = filteredData.filter(x => filterData.itemTypes.map(it => it.id).includes(x.item_type));
            }
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.release_year?.toString().includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(minuteByMinuteDataMemo);

    const csvHeaders = ["Song Start Time", "Artist", "Title", "Release Year", "Type", "Item ID"];
    const csvData = useMemo(
        () =>
            sortedItems?.map(x => [moment(x.played_at).format("DD-MM-YYYY HH:mm:ss"), x.artist, x.title, x.release_year, mapItemType(x.item_type), x.item_id]),
        [sortedItems]
    );

    return (
        <ModulePage>
            <ModulePage.Title>Minute By Minute</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length || !formData.dateRange || !formData.days.length}>
                <InputSelection.Main>
                    <StationPicker formData={formData} setFormData={setFormData} name={moduleName} includeCompetitors={true} loadOnChange={loadFormData} />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Minute by Minute"}
                        whatList={[
                            "A simple time stamped list of everything that happened in an hour on any station",
                            "Filter by specific items types – music, talk, news (where available)",
                            "Filter for specific song titles, artists, or just words in the search bar above the table",
                        ]}
                        knowList={[
                            { text: "You can also open minute by minute under Clocks with right click" },
                            { text: "Search for entire days or weeks, and limit to certain hours or shows" },
                            { text: "Clicking on a song will open the song catalogue, where you can listen to the song if you aren’t familiar with it" },
                        ]}
                        videoSrc="https://www.youtube.com/embed/7DRRRRPCtGs?si=OQxIJ6fiHfvxM5mL"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename="Minute By Minute">
                            <DataDescription>
                                <DataDescription.Station radioId={data[0]?.radio_id} />
                                <DataDescription.DateRange startDate={data[0].played_at} endDate={data[data.length - 1].played_at} />
                            </DataDescription>
                        </DescriptionWithDownloadRow>
                        <Col xs={12} className="mb-2">
                            <Row className=" gy-2">
                                <Col xs={12} xl={5} className=" align-content-end">
                                    <SongSearchInput filterData={filterData} setFilterData={setFilterData} />
                                </Col>
                                <Col>
                                    <ItemTypesPicker
                                        formData={filterData}
                                        setFormData={setFilterData}
                                        name={moduleName}
                                        filterFunction={x => x.id !== "b"}
                                        inputOnly
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <MinuteByMinuteTable sortConfig={sortConfig} requestSort={requestSort} data={sortedItems} />
                    </>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
